import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ListOrdered } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTheme } from '../../store/theme-context';

interface JsonContentProps {
  children: string;
  maxHeight?: string;
  showHeader?: boolean;
  className?: string;
}

export function JsonContent({ children, maxHeight, showHeader = true, className = '' }: JsonContentProps) {
  const { resolvedTheme } = useTheme();
  const [copied, setCopied] = useState(false);
  // JSON viewer ships with line numbers on by default — the toggle in
  // the header lets the user dismiss them for a cleaner copy/paste view.
  const [lineNumbersOn, setLineNumbersOn] = useState(true);

  // Pretty-print if valid JSON; otherwise show raw
  let formatted: string;
  try {
    formatted = JSON.stringify(JSON.parse(children), null, 2);
  } catch {
    formatted = children;
  }

  // Compute per-line indent so wrapped text aligns to the value start
  const lines = formatted.split('\n');
  const lineIndents = lines.map((line) => {
    const colonIdx = line.indexOf('": ');
    if (colonIdx === -1) {
      // structural lines ({, }, [, ]) — indent to leading whitespace
      const leading = line.match(/^\s*/)?.[0].length ?? 0;
      return leading;
    }
    // value starts after '": ' (3 chars past the colon position)
    return colonIdx + 3;
  });

  const theme = resolvedTheme === 'dark' ? nord : oneLight;

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(formatted);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`overflow-hidden rounded border border-border ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between border-b border-border bg-muted px-3 py-1.5">
          <span className="text-xs font-mono text-muted-foreground">JSON</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setLineNumbersOn((v) => !v)}
              aria-pressed={lineNumbersOn}
              aria-label={lineNumbersOn ? 'Hide line numbers' : 'Show line numbers'}
              title={lineNumbersOn ? 'Hide line numbers' : 'Show line numbers'}
              className={cn(
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs hover:bg-accent hover:text-foreground',
                lineNumbersOn ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              <ListOrdered className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={copyToClipboard}
              aria-label={copied ? 'Copied' : 'Copy to clipboard'}
              title={copied ? 'Copied' : 'Copy to clipboard'}
              className="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
      )}
      <div className="overflow-auto" style={maxHeight ? { maxHeight } : undefined}>
        <SyntaxHighlighter
          language="json"
          style={theme}
          showLineNumbers={lineNumbersOn}
          wrapLines
          wrapLongLines
          lineProps={(lineNumber: number) => {
            const indent = (lineIndents[lineNumber - 1] ?? 0) + 1;
            return { style: { display: 'block', paddingLeft: `${indent}ch`, textIndent: `-${indent}ch` } };
          }}
          customStyle={{
            margin: 0,
            padding: '0.75rem',
            background: 'hsl(var(--card))',
            fontSize: '0.75rem',
            lineHeight: '1.5',
          }}
          codeTagProps={{
            style: {
              background: 'transparent',
              textShadow: 'none',
            },
          }}
          lineNumberStyle={{
            color: 'hsl(var(--muted-foreground))',
            paddingRight: '1rem',
            userSelect: 'none',
            background: 'transparent',
          }}
        >
          {formatted}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
